import { useAuth } from '@auth/useAuth'
import AppPaths from '@globals/paths'
import AnonPaths from '@globals/paths/anon'
import {
  INCORRECT_HTML_CONTENT_TYPE_MESSAGE,
  register404Listener,
  register503Listener,
  registerIncorrectHtmlContentTypeListener,
  registerRewardEmailRequiredListener,
  unauthenticatedListener,
} from '@kudos/http-client'
import RouteLoading from '@shared/routing/RouteLoading'
import { useEffect, useRef, useState } from 'react'
import { Location, Outlet, useLocation, useNavigate } from 'react-router'
import RootLayout from '../layouts/RootLayout'

export const AwaitAuth = () => {
  const { isAuthenticated, isLoading, initializeFromStorage, clearAuth, isTenantScope } = useAuth()
  const hasInitialized = useRef<boolean>(false)
  const tenantScopeRef = useRef<boolean>(isTenantScope)
  const navigate = useNavigate()
  const location = useLocation()
  // Kept current so the global RewardEmailRequired listener (registered once)
  // always redirects from — and back to — the user's actual location.
  const locationRef = useRef<Location>(location)
  // Async HTML content-type failures cannot be caught by ErrorBoundary directly;
  // store the error and rethrow during render so ErrorBoundaryUI is shown.
  // Use the exported constant so ErrorBoundaryUI can identify this error and reload instead of
  // soft-resetting (which would immediately rethrow the same error).
  const [incorrectHtmlContentTypeError, setIncorrectHtmlContentTypeError] = useState<Error | null>(
    null,
  )

  useEffect(() => {
    tenantScopeRef.current = isTenantScope
  }, [isTenantScope])

  useEffect(() => {
    locationRef.current = location
  }, [location])

  useEffect(() => {
    if (!hasInitialized.current) {
      initializeFromStorage()
      // Register listeners before session restore triggers API calls (e.g. /apps via useApps).
      unauthenticatedListener(() => {
        if (tenantScopeRef.current) {
          clearAuth()
        }
      })
      // Register listener to detect 503 (maintenance) and redirect to maintenance page
      register503Listener(() => navigate(AnonPaths.maintenance()))
      // Register listener to detect 404 (not found) and redirect to not found page
      register404Listener(() => navigate(AnonPaths.notFound(), { replace: true }))
      // WAF / proxy block pages often return text/html instead of JSON
      registerIncorrectHtmlContentTypeListener(() => {
        setIncorrectHtmlContentTypeError(new Error(INCORRECT_HTML_CONTENT_TYPE_MESSAGE))
      })
      // Centralised front door (epic SYN-7280): any reward endpoint returning
      // RewardEmailRequired sends a deskless member to the email-capture screen.
      registerRewardEmailRequiredListener(() => {
        const current = locationRef.current
        const { pathname } = current
        // Never redirectfrom the capture screen itself (which would loop and clobber `from`).
        if (pathname === AppPaths.tenant.rewardsEmailRequired()) {
          return
        }
        navigate(AppPaths.tenant.rewardsEmailRequired(), {
          state: { from: `${current.pathname}${current.search}` },
        })
      })
    }
    hasInitialized.current = true
  }, [clearAuth, initializeFromStorage, navigate])

  if (incorrectHtmlContentTypeError) {
    throw incorrectHtmlContentTypeError
  }

  if (isLoading) {
    return <RouteLoading />
  }

  return (
    <RootLayout isAuthenticated={isAuthenticated} isTenantScope={isTenantScope}>
      <Outlet />
    </RootLayout>
  )
}
